import { ActionButton, Panel, PanelType, Pivot, PivotItem, Stack, Text } from '@fluentui/react';
import * as React from 'react';

import { CDMRecord } from '../../../Common/DataOwnerModels/CDMRecord';
import { CompiledExpression, Rule, RuleSetRecord } from '../../../Common/DataOwnerModels/RuleSetRecord';
import * as Styled from '../../../Common/Styles/viewRequest.styled';
import {
  displayValue,
  EM_DASH,
  ExpressionLanguage,
  expressionLanguageLabels,
  getEnvironmentLabel,
  getExpressionText,
} from './RuleSetHelpers';

export type ViewRuleSetPanelProps = {
  isOpen: boolean;
  ruleSet: RuleSetRecord;
  cdmList: CDMRecord[];
  dismissPanel: () => void;
};

const expressionCodeStyle: React.CSSProperties = {
  margin: 0,
  padding: 12,
  background: '#f3f2f1',
  fontFamily: 'Consolas, monospace',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

type ExpressionViewerProps = {
  label: string;
  expression?: CompiledExpression;
  language: ExpressionLanguage;
};

function ExpressionViewer(props: ExpressionViewerProps): React.ReactElement {
  const { label, expression, language } = props;
  return (
    <Stack tokens={{ childrenGap: 4 }}>
      <Styled.BasicDetailsField>{label}</Styled.BasicDetailsField>
      <pre style={expressionCodeStyle} aria-label={`${label} (${expressionLanguageLabels[language]})`}>
        {getExpressionText(expression, language)}
      </pre>
    </Stack>
  );
}

const renderDetailRow = (field: string, value: string): React.ReactElement => (
  <Styled.BasicDetailsSection key={field}>
    <Styled.BasicDetailsField>{field}</Styled.BasicDetailsField>
    <Styled.BasicDetailsValue>{value}</Styled.BasicDetailsValue>
  </Styled.BasicDetailsSection>
);

const formatOptional = (optional?: boolean): string => {
  if (optional === true) {
    return 'Yes';
  }
  if (optional === false) {
    return 'No';
  }
  return EM_DASH;
};

export function ViewRuleSetPanel(props: ViewRuleSetPanelProps): React.ReactElement {
  const { isOpen, ruleSet, cdmList, dismissPanel } = props;
  const [isExpressionsExpanded, setIsExpressionsExpanded] = React.useState<boolean>(false);
  const [expressionLanguage, setExpressionLanguage] = React.useState<ExpressionLanguage>('cSharp');

  const entityName =
    cdmList?.find((cdm) => cdm.id === ruleSet?.dataSource?.entityTypeId)?.name ?? ruleSet?.dataSource?.entityTypeId;

  const rules: Rule[] = ruleSet?.rules ?? [];

  return (
    <Panel
      isOpen={isOpen}
      type={PanelType.medium}
      onDismiss={dismissPanel}
      isLightDismiss
      headerText={displayValue(ruleSet?.name)}
      closeButtonAriaLabel="Close rule set details"
      styles={Styled.stickyFooterPanelStyles}
    >
      <Styled.ViewRequestContainer data-testid="view-rule-set-panel" aria-label="Rule set details">
        <Styled.BasicDetailsContainer>
          <Styled.SubSectionalHeading>
            <Styled.SubSectionalHeadingText>Rule Set Details</Styled.SubSectionalHeadingText>
            <Styled.LineDivider />
          </Styled.SubSectionalHeading>
          {renderDetailRow('Name', displayValue(ruleSet?.name))}
          {renderDetailRow('Description', displayValue(ruleSet?.description))}
          {renderDetailRow('Version', displayValue(ruleSet?.version))}
          {renderDetailRow('Status', displayValue(ruleSet?.status))}
          {renderDetailRow('Environment', getEnvironmentLabel(ruleSet?.environment))}
          {renderDetailRow('Category', displayValue(ruleSet?.category))}
          {renderDetailRow('Type', displayValue(ruleSet?.ruleSetType))}
          {renderDetailRow('Tags', displayValue(ruleSet?.tags))}
          {renderDetailRow('Data Source Entity', displayValue(entityName))}
        </Styled.BasicDetailsContainer>

        <Styled.RuleSetsDetailsContainer>
          <Styled.SubSectionalHeading>
            <Styled.SubSectionalHeadingText>{`Rules (${rules.length})`}</Styled.SubSectionalHeadingText>
            <Styled.LineDivider />
          </Styled.SubSectionalHeading>
          {rules.length === 0 && <Text>No rules defined.</Text>}
          {rules.map((rule, index) => (
            <Stack key={rule.ruleId ?? index} tokens={{ childrenGap: 8 }} data-testid="rule-set-rule">
              {renderDetailRow('Rule Name', displayValue(rule.ruleName))}
              {renderDetailRow('Error Message', displayValue(rule.errorMessage))}
              {renderDetailRow('Optional', formatOptional(rule.optional))}
              {renderDetailRow('Data Quality', displayValue(rule.dataQuality))}
              {index < rules.length - 1 && <Styled.LineDivider />}
            </Stack>
          ))}
        </Styled.RuleSetsDetailsContainer>

        <Styled.RuleSetsDetailsContainer>
          <ActionButton
            iconProps={{ iconName: isExpressionsExpanded ? 'ChevronDown' : 'ChevronRight' }}
            aria-expanded={isExpressionsExpanded}
            aria-controls="rule-set-expressions"
            onClick={() => setIsExpressionsExpanded(!isExpressionsExpanded)}
          >
            {isExpressionsExpanded ? 'Hide expressions' : 'Show expressions'}
          </ActionButton>
          {isExpressionsExpanded && (
            <Stack id="rule-set-expressions" tokens={{ childrenGap: 12 }}>
              <Pivot
                aria-label="Expression language"
                selectedKey={expressionLanguage}
                onLinkClick={(item?: PivotItem) =>
                  item && setExpressionLanguage(item.props.itemKey as ExpressionLanguage)
                }
              >
                {(Object.keys(expressionLanguageLabels) as ExpressionLanguage[]).map((language) => (
                  <PivotItem key={language} itemKey={language} headerText={expressionLanguageLabels[language]} />
                ))}
              </Pivot>
              <ExpressionViewer
                label="Filter Expression"
                expression={ruleSet?.filterExpression?.compiledExpression}
                language={expressionLanguage}
              />
              {rules.map((rule, index) => (
                <ExpressionViewer
                  key={rule.ruleId ?? index}
                  label={`Rule: ${displayValue(rule.ruleName)}`}
                  expression={rule.expression?.compiledExpression}
                  language={expressionLanguage}
                />
              ))}
            </Stack>
          )}
        </Styled.RuleSetsDetailsContainer>
      </Styled.ViewRequestContainer>
    </Panel>
  );
}
